<template>
  <DashboardContainer :pageTitle="pageTitle">
    <!-- Spinner for loading state -->
    <GivarooSpinner v-if="loading" class="mt-24" />

    <template v-else>
      <!-- Date Inputs -->
      <div class="flex flex-wrap gap-4 mb-4">
        <div class="flex items-center gap-2 flex-grow">
          <label for="start" class="text-gray-700">Start Date:</label>
          <FlatPickr
            id="start"
            name="start"
            class="input--text--small placeholder:text-gray-400 date-input flex-grow"
            placeholder="Select date start"
            :value="selectedDate.date1"
            @input="handleDateChange('date1', $event.target.value)"
          />
        </div>
        <div class="flex items-center gap-2 flex-grow">
          <label for="end" class="text-gray-700">End Date:</label>
          <FlatPickr
            id="end"
            name="end"
            class="input--text--small placeholder:text-gray-400 date-input flex-grow"
            placeholder="Select date end"
            :value="selectedDate.date2"
            @input="handleDateChange('date2', $event.target.value)"
          />
        </div>
      </div>

      <!-- Overview section + KPI cards -->
      <div class="mb-4">
        <h2 class="text-xl font-semibold text-gray-800 font-jungle tracking-widest leading-relaxed">
          Overview:
        </h2>
      </div>
      <div v-if="kpis.length" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <div
          v-for="card in kpis"
          :key="card.title"
          class="rounded-xl p-5 text-white shadow flex flex-col gap-2"
          :class="card.bg"
        >
          <div class="text-sm/5 opacity-90">{{ card.title }}</div>
          <div class="text-3xl font-bold">{{ card.displayValue }}</div>
          <div v-if="card.subtext" class="text-xs/5 opacity-90">{{ card.subtext }}</div>
        </div>
      </div>

      <!-- Navigation -->
      <div class="flex flex-wrap gap-4 mb-4">
        <div class="flex items-center gap-2 flex-grow">
          <label for="start" class="text-gray-700 mr-2">Campaign Filter:</label>
          <multiselect
            v-model="selectedCampaignFilter"
            :options="campaignFilters"
            :multiple="true"
            :close-on-select="true"
            placeholder="Select Campaigns"
            selectLabel="Apply"
            deselectLabel="Remove"
            :custom-label="customLabel"
            track-by="id"
            clearable
          ></multiselect>
        </div>
        <div class="flex items-center gap-2 flex-grow">
          <label for="fundraiser" class="text-gray-700 mr-2">Fundraiser:</label>
          <multiselect
            v-model="selectedFundraiserFilter"
            :options="fundraiserFilters"
            :multiple="true"
            :close-on-select="false"
            placeholder="Select Fundraisers"
            selectLabel="Apply"
            deselectLabel="Remove"
            label="full_name"
            track-by="id"
            :loading="isSearchingFundraisers"
            :searchable="true"
            :internal-search="false"
            @search-change="handleFundraiserSearch"
          >
            <template v-slot:noResult>
              <span>No fundraisers found. Try a different search.</span>
            </template>
            <template v-slot:noOptions>
              <span>Type to search for fundraisers</span>
            </template>
          </multiselect>
        </div>
      </div>

      <div class="flex justify-between items-center mb-4">
        <div class="flex items-center gap-4">
          <h1
            style="
              font-family: 'JungleFever';
              font-size: 1.5rem;
              font-weight: bold;
              text-decoration: underline;
              letter-spacing: 0.1em;
            "
          >
            Fundraiser Metrics
          </h1>
          <RouterLink
            :to="{
              name: 'CampaignCharts',
              query: { start: selectedDate.date1, end: selectedDate.date2 },
            }"
            class="btn--table"
          >
            Campaign Metrics
          </RouterLink>
        </div>
      </div>

      <!-- Recurring Donations Section -->
      <h2 class="text-xl font-bold mb-4 font-jungle tracking-widest leading-relaxed">
        Recurring Donations
      </h2>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div class="scrollable-chart-container">
          <h3 class="title font-bold mb-4 text-center">Number of Donations</h3>
          <div class="chart-outer-wrapper">
            <div class="chart-inner-wrapper" :style="{ minWidth: dynamicChartWidth }">
              <BarChart
                :chart-data="numberOfDonationsData"
                :options="chartOptions"
                v-if="numberOfDonationsData.labels.length > 0"
              />
              <div v-else class="flex items-center justify-center h-full text-gray-500">
                No data available
              </div>
            </div>
          </div>
        </div>

        <!-- Repeat similar structure for other recurring donation charts -->
        <div class="scrollable-chart-container">
          <h3 class="title font-bold mb-4 text-center">Donors Over 30</h3>
          <div class="chart-outer-wrapper">
            <div class="chart-inner-wrapper" :style="{ minWidth: dynamicChartWidth }">
              <BarChart
                :chart-data="donorsOver30Data"
                :options="chartOptions"
                v-if="donorsOver30Data.labels.length > 0"
              />
              <div v-else class="flex items-center justify-center h-full text-gray-500">
                No data available
              </div>
            </div>
          </div>
        </div>

        <div class="scrollable-chart-container">
          <h3 class="title font-bold mb-4 text-center">Average Donor Age</h3>
          <div class="chart-outer-wrapper">
            <div class="chart-inner-wrapper" :style="{ minWidth: dynamicChartWidth }">
              <BarChart
                :chart-data="averageDonorAgeData"
                :options="chartOptions"
                v-if="averageDonorAgeData.labels.length > 0"
              />
              <div v-else class="flex items-center justify-center h-full text-gray-500">
                No data available
              </div>
            </div>
          </div>
        </div>

        <div class="scrollable-chart-container">
          <h3 class="title font-bold mb-4 text-center">Average Donation Amount</h3>
          <div class="chart-outer-wrapper">
            <div class="chart-inner-wrapper" :style="{ minWidth: dynamicChartWidth }">
              <BarChart
                :chart-data="averageDonationAmountData"
                :options="chartOptions"
                v-if="averageDonationAmountData.labels.length > 0"
              />
              <div v-else class="flex items-center justify-center h-full text-gray-500">
                No data available
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- One-Time Donations Section -->
      <h2 class="text-xl font-bold mb-4 font-jungle tracking-widest leading-relaxed">
        One-Time Donations
      </h2>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div class="scrollable-chart-container">
          <h3 class="title font-bold mb-4 text-center">Number of One-Time Donations</h3>
          <div class="chart-outer-wrapper">
            <div class="chart-inner-wrapper" :style="{ minWidth: dynamicChartWidth }">
              <BarChart
                :chart-data="numberOfOneTimeDonationsData"
                :options="chartOptions"
                v-if="numberOfOneTimeDonationsData.labels.length > 0"
              />
              <div v-else class="flex items-center justify-center h-full text-gray-500">
                No data available
              </div>
            </div>
          </div>
        </div>

        <div class="scrollable-chart-container">
          <h3 class="title font-bold mb-4 text-center">Average One-Time Donation Amount</h3>
          <div class="chart-outer-wrapper">
            <div class="chart-inner-wrapper" :style="{ minWidth: dynamicChartWidth }">
              <BarChart
                :chart-data="averageOneTimeDonationAmountData"
                :options="chartOptions"
                v-if="averageOneTimeDonationAmountData.labels.length > 0"
              />
              <div v-else class="flex items-center justify-center h-full text-gray-500">
                No data available
              </div>
            </div>
          </div>
        </div>

        <div class="scrollable-chart-container">
          <h3 class="title font-bold mb-4 text-center">One-Time Donation Sum</h3>
          <div class="chart-outer-wrapper">
            <div class="chart-inner-wrapper" :style="{ minWidth: dynamicChartWidth }">
              <BarChart
                :chart-data="oneTimeDonationSumData"
                :options="chartOptions"
                v-if="oneTimeDonationSumData.labels.length > 0"
              />
              <div v-else class="flex items-center justify-center h-full text-gray-500">
                No data available
              </div>
            </div>
          </div>
        </div>
        <!-- Form Type Distribution Pie Chart -->
        <div class="pie-chart-container">
          <h3 class="title font-bold mb-4 text-center">Form Type Distribution</h3>
          <div class="chart-outer-wrapper">
            <PieChart
              :chart-data="formTypePieChartData"
              :options="pieChartOptions"
              v-if="formTypePieChartData.labels.length > 0"
            />
            <div v-else class="flex items-center justify-center h-full text-gray-500">
              No data available
            </div>
          </div>
        </div>
      </div>

      <!-- Donor Verification Section -->
      <h2 class="text-xl font-bold mb-4 font-jungle tracking-widest leading-relaxed">
        Donor Verification
      </h2>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div class="scrollable-chart-container">
          <h3 class="title font-bold mb-4 text-center">DV SMS Sent by Fundraisers</h3>
          <div class="chart-outer-wrapper">
            <div class="chart-inner-wrapper" :style="{ minWidth: dynamicChartWidth }">
              <BarChart
                :chart-data="dvSmsSentData"
                :options="chartOptions"
                v-if="dvSmsSentData.labels.length > 0"
              />
              <div v-else class="flex items-center justify-center h-full text-gray-500">
                No data available
              </div>
            </div>
          </div>
        </div>

        <div class="scrollable-chart-container">
          <h3 class="title font-bold mb-4 text-center">DV SMS Verified by Fundraisers</h3>
          <div class="chart-outer-wrapper">
            <div class="chart-inner-wrapper" :style="{ minWidth: dynamicChartWidth }">
              <BarChart
                :chart-data="dvSmsVerifiedData"
                :options="chartOptions"
                v-if="dvSmsVerifiedData.labels.length > 0"
              />
              <div v-else class="flex items-center justify-center h-full text-gray-500">
                No data available
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Shifts Worked Section -->
      <h2 class="text-xl font-bold mb-4 font-jungle tracking-widest leading-relaxed">Shifts</h2>
      <div class="grid grid-cols-1 gap-4 mb-4">
        <div class="scrollable-chart-container">
          <h3 class="title font-bold mb-4 text-center">Shifts by Fundraisers</h3>
          <div class="chart-outer-wrapper">
            <div class="chart-inner-wrapper" :style="{ minWidth: dynamicChartWidth }">
              <BarChart
                :chart-data="shiftsSpentData"
                :options="chartOptions"
                v-if="shiftsSpentData.labels.length > 0"
              />
              <div v-else class="flex items-center justify-center h-full text-gray-500">
                No data available
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- M1 % Metrics Section -->
      <h2 class="text-xl font-bold mb-4 font-jungle tracking-widest leading-relaxed">
        M1 % Metrics
      </h2>
      <div class="grid grid-cols-1 gap-4 mb-4">
        <div class="scrollable-chart-container">
          <h3 class="title font-bold mb-4 text-center">M1 Percentage by Fundraiser</h3>
          <div class="chart-outer-wrapper">
            <div class="chart-inner-wrapper" :style="{ minWidth: dynamicChartWidth }">
              <BarChart
                :chart-data="m1BarChartData"
                :options="chartOptions"
                v-if="m1BarChartData.labels.length > 0"
              />
              <div v-else class="flex items-center justify-center h-full text-gray-500">
                No data available
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div class="pie-chart-container">
          <h3 class="title font-bold mb-4 text-center">Donor Age Distribution</h3>
          <div class="chart-outer-wrapper">
            <PieChart
              :chart-data="donorAgePieChartData"
              :options="agePieChartOptions"
              v-if="donorAgePieChartData.labels.length > 0"
            />
            <div v-else class="flex items-center justify-center h-full text-gray-500">
              No data available
            </div>
          </div>
        </div>
        <div class="pie-chart-container">
          <h3 class="title font-bold mb-4 text-center">Donation Amount Distribution</h3>
          <div class="chart-outer-wrapper">
            <PieChart
              :chart-data="donationAmountPieChartData"
              :options="pieChartOptions"
              v-if="donationAmountPieChartData.labels.length > 0"
            />
            <div v-else class="flex items-center justify-center h-full text-gray-500">
              No data available
            </div>
          </div>
        </div>
      </div>

      <h2
        v-if="showStopsByFundraiserChart"
        class="text-xl font-bold mb-4 font-jungle tracking-widest leading-relaxed"
      >
        Stops
      </h2>
      <div v-if="showStopsByFundraiserChart" class="grid grid-cols-1 gap-4 mb-4">
        <div class="scrollable-chart-container">
          <h3 class="title font-bold mb-4 text-center">Stops Count by Fundraiser</h3>
          <div class="chart-outer-wrapper">
            <div class="chart-inner-wrapper" :style="{ minWidth: dynamicChartWidth }">
              <BarChart
                :chart-data="stopsByFundraiserData"
                :options="chartOptions"
                v-if="stopsByFundraiserData.labels.length > 0"
              />
              <div v-else class="flex items-center justify-center h-full text-gray-500">
                No data available
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Back Button -->
      <div class="flex justify-end mt-10">
        <RouterLink :to="{ name: 'CampaignList' }" class="btn--table">
          Back to Campaigns
        </RouterLink>
      </div>
    </template>
  </DashboardContainer>
</template>

<script>
import { ref, onBeforeMount, reactive, computed, watch } from 'vue'
import { BarChart, PieChart } from 'vue-chart-3'
import { Chart, registerables } from 'chart.js'
import DashboardContainer from '../components/DashboardContainer.vue'
import Spinner from '@/components/Spinner'
import { DonationApi } from '@/services/donations/'
import { WorkedHoursApi } from '@/services/workingHours'
import { useStore } from 'vuex'
import FlatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import Multiselect from 'vue-multiselect'
import 'vue-multiselect/dist/vue-multiselect.css'
import { useRoute } from 'vue-router'
import GivarooSpinner from '@/components/GivarooSpinner.vue'
import { getTodayInEastern } from '@/utils/dateHelpers'

Chart.register(...registerables)

export default {
  name: 'FundraiserPerformanceCharts',
  components: {
    DashboardContainer,
    Spinner,
    BarChart,
    PieChart,
    FlatPickr,
    Multiselect,
    GivarooSpinner,
  },
  setup() {
    const store = useStore()
    const loading = ref(false)
    const route = useRoute()
    const today = getTodayInEastern()
    const selectedDate = reactive({
      date1: today,
      date2: '',
    })
    const pageTitle = ref('Fundraiser Performance Charts')
    const filterIndexes = reactive({
      campaigns: [],
      fundraisers: [],
    })
    const campaignFilters = computed(() => filterIndexes.campaigns)
    const selectedCampaignFilter = ref([])
    const fundraiserFilters = computed(() => filterIndexes.fundraisers)
    const selectedFundraiserFilter = ref([])
    const fundraiserSearchQuery = ref('')
    const isSearchingFundraisers = ref(false)

    const campaignFilterValue = ref('')
    const showStopsByFundraiserChart = computed(() => !store.getters.isNPOAdmin)
    const setFilter = () => {
      campaignFilterValue.value = selectedCampaignFilter.value
        .map((campaign) => campaign.id)
        .join(',')
      // Add fundraiser filter
      const fundraiserFilterValue = selectedFundraiserFilter.value
        .map((fundraiser) => fundraiser.id)
        .join(',')
      fetchData(
        selectedDate.date1,
        selectedDate.date2,
        campaignFilterValue.value,
        fundraiserFilterValue,
      )
    }
    const customLabel = (option) => {
      return `${option.name} - ${option.location}`
    }
    // Add a debounce function
    const debounce = (fn, delay) => {
      let timeoutId
      return function (...args) {
        clearTimeout(timeoutId)
        timeoutId = setTimeout(() => fn.apply(this, args), delay)
      }
    }
    // Add debounced search function
    const searchFundraisers = debounce(async (query) => {
      if (!query || query.length < 2) {
        filterIndexes.fundraisers = []
        return
      }

      isSearchingFundraisers.value = true
      try {
        const response = await DonationApi.csc.searchFundraisers({ query })
        filterIndexes.fundraisers = response
      } catch (error) {
        console.error('Error searching fundraisers:', error)
      } finally {
        isSearchingFundraisers.value = false
      }
    }, 300)

    // Watch for changes in search query
    watch(fundraiserSearchQuery, (newQuery) => {
      searchFundraisers(newQuery)
    })

    const handleFundraiserSearch = (query) => {
      fundraiserSearchQuery.value = query
    }

    // UI helpers
    const kpis = ref([])
    const formatNumber = (value) => new Intl.NumberFormat('en-US').format(Number(value || 0))
    const formatCurrency = (value) =>
      new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      }).format(Number(value || 0))

    // Initialize chart data with empty structure
    const initializeChartData = (label, backgroundColor) => ({
      labels: [],
      datasets: [
        {
          label,
          backgroundColor,
          maxBarThickness: 32,
          data: [],
        },
      ],
    })

    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { left: 0, right: 0, top: 0, bottom: 0 } },

      scales: {
        y: {
          beginAtZero: true,
          grid: {
            color: 'rgba(0,0,0,0.06)',
            drawBorder: false,
          },
          ticks: {
            callback: (val) => new Intl.NumberFormat('en-US').format(val),
            color: '#6b7280',
            font: {
              family: 'JungleFever',
              size: 12,
            },
          },
        },
        x: {
          grid: { display: false, drawBorder: false },
          ticks: {
            autoSkip: false, // show all labels
            maxRotation: 45,
            minRotation: 45,
            color: '#6b7280',
            categoryPercentage: 1.0, // Controls space between categories
            font: {
              family: 'JungleFever',
              size: 12,
            },
          },
        },
      },
      plugins: {
        legend: {
          display: false,
          position: 'top',
          labels: { usePointStyle: true, boxWidth: 8, color: '#374151' },
        },
        tooltip: {
          backgroundColor: 'rgba(31,41,55,0.9)',
          titleColor: '#fff',
          bodyColor: '#fff',
          callbacks: {
            label: (context) => {
              const label = context.dataset.label || ''
              const value = context.raw || 0
              const formattedValue = new Intl.NumberFormat('en-US').format(value)
              // Add % symbol for M1 Percentage
              if (label === 'M1 Percentage') {
                return `${label}: ${formattedValue}%`
              }
              return `${label}: ${formattedValue}`
            },
          },
        },
      },
      elements: {
        bar: {
          borderRadius: 8,
        },
      },
    }

    const buildAdminKpis = (metrics, shiftsResponse) => {
      const totalDonorsOver30 = metrics.reduce(
        (sum, c) => sum + Number(c.recurring_metrics?.donors_over_30 || 0),
        0,
      )

      const averageOneTimeDonation = (() => {
        const totalSum = metrics.reduce(
          (sum, c) => sum + Number(c.one_time_metrics?.total_donation_sum || 0),
          0,
        )
        const totalCount = metrics.reduce(
          (count, c) => count + Number(c.one_time_metrics?.number_of_donations || 0),
          0,
        )
        return totalCount > 0 ? totalSum / totalCount : 0
      })()

      const averageRecurringDonation = (() => {
        const totalAmount = metrics.reduce(
          (sum, c) =>
            sum +
            Number(c.recurring_metrics?.average_donation_amount || 0) *
              Number(c.recurring_metrics?.number_of_donations || 0),
          0,
        )
        const totalCount = metrics.reduce(
          (count, c) => count + Number(c.recurring_metrics?.number_of_donations || 0),
          0,
        )
        return totalCount > 0 ? totalAmount / totalCount : 0
      })()

      const averageShiftsPerFundraiser = (() => {
        if (!shiftsResponse || !shiftsResponse.length) return 0

        let totalShifts = 0
        let totalFundraisers = shiftsResponse.length

        shiftsResponse.forEach((fundraiser) => {
          totalShifts += Number(fundraiser.total_shifts || 0)
        })

        return totalFundraisers > 0 ? (totalShifts / totalFundraisers).toFixed(2) : 0
      })()

      return [
        {
          title: 'Total Donors Over 30',
          displayValue: formatNumber(totalDonorsOver30),
          bg: 'bg-blue-500',
          subtext: 'For selected range',
        },
        {
          title: 'Avg One-Time Donation',
          displayValue: formatCurrency(averageOneTimeDonation),
          bg: 'bg-yellow-500',
          subtext: 'For selected range',
        },
        {
          title: 'Avg Recurring Donation',
          displayValue: formatCurrency(averageRecurringDonation),
          bg: 'bg-cyan-500',
          subtext: 'For selected range',
        },
        {
          title: 'Avg Shifts per Fundraiser',
          displayValue: formatNumber(averageShiftsPerFundraiser),
          bg: 'bg-green-500',
          subtext: 'For selected range',
        },
      ]
    }

    const dynamicChartWidth = computed(() => {
      const labelCount = numberOfDonationsData.value.labels.length
      return `${Math.max(labelCount * 100, 800)}px` // Minimum width of 800px
    })

    // Chart data refs
    const numberOfDonationsData = ref(initializeChartData('Number of Donations', '#2596be'))
    const donorsOver30Data = ref(initializeChartData('Donors Over 30', '#2596be'))
    const averageDonorAgeData = ref(initializeChartData('Average Donor Age', '#2596be'))
    const averageDonationAmountData = ref(initializeChartData('Average Donation Amount', '#2596be'))
    const numberOfOneTimeDonationsData = ref(
      initializeChartData('Number of One-Time Donations', '#2596be'),
    )
    const averageOneTimeDonationAmountData = ref(
      initializeChartData('Average One-Time Donation Amount', '#2596be'),
    )
    const oneTimeDonationSumData = ref(initializeChartData('One-Time Donation Sum', '#2596be'))
    const shiftsSpentData = ref(initializeChartData('Shifts', '#2596be'))
    const stopsByFundraiserData = ref(initializeChartData('Stops Count', '#2596be'))

    // Form Type Pie Chart Data
    const formTypePieChartData = ref({
      labels: ['QR Code', 'Fundraiser Tablet'],
      datasets: [
        {
          data: [0, 0],
          backgroundColor: ['#2596be', '#10b981'],
        },
      ],
    })
    const donorAgePieChartData = ref({
      labels: [],
      datasets: [
        {
          data: [],
          backgroundColor: [
            // 25-29
            '#36A2EB',
            '#36A2EB',
            '#36A2EB',
            '#36A2EB',
            '#36A2EB',
            // 30-39
            '#FFCE56',
            '#FFCE56',
            '#FFCE56',
            '#FFCE56',
            '#FFCE56',
            '#FFCE56',
            '#FFCE56',
            '#FFCE56',
            '#FFCE56',
            '#FFCE56',
            // 40+
            '#4BC0C0',
            '#9966FF',
            '#FF9F40',
          ],
        },
      ],
    })

    const m1BarChartData = ref({
      labels: [],
      datasets: [
        {
          label: 'M1 Percentage',
          backgroundColor: '#2596be',
          borderRadius: 8,
          maxBarThickness: 32,
          data: [],
        },
      ],
    })

    const donationAmountPieChartData = ref({
      labels: [],
      datasets: [
        {
          data: [],
          backgroundColor: [
            '#9966FF',
            '#00FF00',
            '#0000FF',
            '#FFFF00',
            '#FF00FF',
            '#00FFFF',
            '#FFA500',
            '#800080',
            '#32CD32',
            '#FFC0CB',
            '#008080',
            '#FF0000',
          ],
        },
      ],
    })

    // Pie chart options
    const pieChartOptions = ref({
      responsive: true,
      maintainAspectRatio: false,
      aspectRatio: 1,
      plugins: {
        legend: {
          position: 'bottom',
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              const label = context.label || ''
              const value = context.raw || 0
              // Show percentage for form type charts
              if (label === 'QR Code' || label === 'Fundraiser Tablet') {
                return `${label}: ${value.toFixed(1)}%`
              }
              return `${label}: ${value}`
            },
          },
        },
      },
    })
    const agePieChartOptions = ref({
      responsive: true,
      maintainAspectRatio: false,
      aspectRatio: 1,
      plugins: {
        legend: {
          position: 'bottom',
          onClick: function (e, legendItem, legend) {
            const chart = legend.chart
            const labels = chart.data.labels
            const meta = chart.getDatasetMeta(0)

            // Toggle visibility of all slices in this age group
            labels.forEach((label, i) => {
              const age = parseInt(label)
              let shouldToggle = false

              // Check if this age belongs to the clicked group
              if (age >= 25 && age <= 29 && legendItem.text.includes('25-29')) {
                shouldToggle = true
              } else if (age >= 30 && age <= 39 && legendItem.text.includes('30-39')) {
                shouldToggle = true
              } else if (age >= 40 && age <= 49 && legendItem.text.includes('40-49')) {
                shouldToggle = true
              } else if (age >= 50 && age <= 59 && legendItem.text.includes('50-59')) {
                shouldToggle = true
              } else if (age >= 65 && legendItem.text.includes('65+')) {
                shouldToggle = true
              }

              if (shouldToggle) {
                meta.data[i].hidden = !meta.data[i].hidden
              }
            })

            chart.update()
          },
          labels: {
            generateLabels: function (chart) {
              const grouped = {}
              const meta = chart.getDatasetMeta(0)

              chart.data.labels.forEach((label, index) => {
                const age = parseInt(label)
                const value = chart.data.datasets[0].data[index] || 0

                if (age >= 25 && age <= 29) {
                  grouped['25-29'] = (grouped['25-29'] || 0) + value
                } else if (age >= 30 && age <= 39) {
                  grouped['30-39'] = (grouped['30-39'] || 0) + value
                } else if (age >= 40 && age <= 49) {
                  grouped['40-49'] = (grouped['40-49'] || 0) + value
                } else if (age >= 50 && age <= 59) {
                  grouped['50-59'] = (grouped['50-59'] || 0) + value
                } else if (age >= 65) {
                  grouped['65+'] = (grouped['65+'] || 0) + value
                }
              })

              return Object.entries(grouped).map(([label, value], index) => {
                // Check if all slices in this group are hidden
                const labels = chart.data.labels
                let allHidden = true

                labels.forEach((labelItem) => {
                  const age = parseInt(labelItem)
                  let belongsToGroup = false

                  if (age >= 25 && age <= 29 && label === '25-29') {
                    belongsToGroup = true
                  } else if (age >= 30 && age <= 39 && label === '30-39') {
                    belongsToGroup = true
                  } else if (age >= 40 && age <= 49 && label === '40-49') {
                    belongsToGroup = true
                  } else if (age >= 50 && age <= 59 && label === '50-59') {
                    belongsToGroup = true
                  } else if (age >= 65 && label === '65+') {
                    belongsToGroup = true
                  }

                  if (belongsToGroup) {
                    const labelIndex = labels.indexOf(labelItem)
                    if (!meta.data[labelIndex].hidden) {
                      allHidden = false
                    }
                  }
                })

                return {
                  text: label,
                  fillStyle:
                    label === '25-29'
                      ? '#36A2EB'
                      : label === '30-39'
                      ? '#FFCE56'
                      : label === '40-49'
                      ? '#4BC0C0'
                      : label === '50-59'
                      ? '#9966FF'
                      : '#FF9F40',
                  strokeStyle:
                    label === '25-29'
                      ? '#36A2EB'
                      : label === '30-39'
                      ? '#FFCE56'
                      : label === '40-49'
                      ? '#4BC0C0'
                      : label === '50-59'
                      ? '#9966FF'
                      : '#FF9F40',
                  lineWidth: 2,
                  hidden: allHidden,
                  index: index,
                }
              })
            },
          },
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              const label = context.label || ''
              const value = context.raw || 0
              return `${label}: ${value}`
            },
          },
        },
      },
    })

    const dvSmsSentData = ref(initializeChartData('DV SMS Sent', '#2596be'))
    const dvSmsVerifiedData = ref(initializeChartData('DV SMS Verified', '#2596be'))

    const fetchData = async (startDate, endDate, campaignFilterValue, fundraiserFilterValue) => {
      loading.value = true
      try {
        const response = await DonationApi.csc.get_fundraiser_performance_metrics_for_admin({
          start_date: startDate,
          end_date: endDate,
          campaignFilterValue,
          fundraiserFilterValue,
        })

        let metrics = []
        if (Array.isArray(response)) {
          metrics = response
        } else if (response?.data && Array.isArray(response.data)) {
          metrics = response.data
        } else if (response?.data?.data && Array.isArray(response.data.data)) {
          metrics = response.data.data
        }

        if (metrics.length === 0) {
          console.warn('No valid metrics data found in response')
        }

        updateChartData(numberOfDonationsData, metrics, 'recurring_metrics.number_of_donations')
        updateChartData(donorsOver30Data, metrics, 'recurring_metrics.donors_over_30')
        updateChartData(averageDonorAgeData, metrics, 'recurring_metrics.average_donor_age')
        updateChartData(
          averageDonationAmountData,
          metrics,
          'recurring_metrics.average_donation_amount',
        )
        updateChartData(
          numberOfOneTimeDonationsData,
          metrics,
          'one_time_metrics.number_of_donations',
        )
        updateChartData(
          averageOneTimeDonationAmountData,
          metrics,
          'one_time_metrics.average_donation_amount',
        )
        updateChartData(oneTimeDonationSumData, metrics, 'one_time_metrics.total_donation_sum')

        // Form Type Pie Chart Data
        const formTypeResponse = await DonationApi.csc.form_type_pie_chart({
          startDate,
          endDate,
          campaignFilterValue,
          fundraiserFilterValue,
        })
        formTypePieChartData.value.labels = formTypeResponse.map((item) => item.label)
        formTypePieChartData.value.datasets[0].data = formTypeResponse.map((item) => item.value)

        // Process DV SMS data
        updateChartData(dvSmsSentData, metrics, 'dv_sms_metrics.sms_sent_count')
        updateChartData(dvSmsVerifiedData, metrics, 'dv_sms_metrics.sms_verified_count')

        // Donor Age Pie Chart
        const donorAgePieChartResponse = await DonationApi.csc.donor_age_pie_chart({
          start_date: startDate,
          end_date: endDate,
          campaignFilterValue,
          fundraiserFilterValue,
        })
        donorAgePieChartData.value.labels = donorAgePieChartResponse.map((item) => item.label)
        donorAgePieChartData.value.datasets[0].data = donorAgePieChartResponse.map(
          (item) => item.value,
        )

        // Donation Amount Pie Chart
        const donationAmountPieChartResponse = await DonationApi.csc.donation_amount_pie_chart({
          start_date: startDate,
          end_date: endDate,
          campaignFilterValue,
          fundraiserFilterValue,
        })
        donationAmountPieChartData.value.labels = donationAmountPieChartResponse.map(
          (item) => item.label,
        )
        donationAmountPieChartData.value.datasets[0].data = donationAmountPieChartResponse.map(
          (item) => item.value,
        )

        // Fetch shifts data
        const shiftsResponse = await WorkedHoursApi.csc.shifts_spent_by_fundraisers({
          startDate,
          endDate,
          campaignFilterValue,
          fundraiserFilterValue,
        })

        // Process shifts data
        if (shiftsResponse && Array.isArray(shiftsResponse)) {
          shiftsSpentData.value.labels = shiftsResponse.map(
            (fundraiser) => `${fundraiser.first_name} ${fundraiser.last_name}`,
          )
          shiftsSpentData.value.datasets[0].data = shiftsResponse.map(
            (fundraiser) => fundraiser.total_shifts,
          )
        } else {
          console.warn('Invalid shifts data format:', shiftsResponse)
          shiftsSpentData.value.labels = []
          shiftsSpentData.value.datasets[0].data = []
        }

        if (showStopsByFundraiserChart.value) {
          try {
            const stopsResponse = await WorkedHoursApi.csc.stops_by_fundraisers({
              startDate,
              endDate,
              campaignFilterValue,
              fundraiserFilterValue,
            })
            if (stopsResponse && Array.isArray(stopsResponse)) {
              stopsByFundraiserData.value.labels = stopsResponse.map(
                (fundraiser) => `${fundraiser.first_name} ${fundraiser.last_name}`,
              )
              stopsByFundraiserData.value.datasets[0].data = stopsResponse.map(
                (fundraiser) => fundraiser.total_stops,
              )
            } else {
              console.warn('Invalid stops data format:', stopsResponse)
              stopsByFundraiserData.value.labels = []
              stopsByFundraiserData.value.datasets[0].data = []
            }
          } catch (error) {
            console.error('Error fetching stops data:', error)
            stopsByFundraiserData.value.labels = []
            stopsByFundraiserData.value.datasets[0].data = []
          }
        } else {
          stopsByFundraiserData.value.labels = []
          stopsByFundraiserData.value.datasets[0].data = []
        }

        // Fetch M1 percentage for each fundraiser
        const m1Percentages = []
        const m1Labels = []

        for (const metric of metrics) {
          try {
            const fundraiserId = metric.fundraiser_id || metric.fundraiser?.id
            if (!fundraiserId) continue

            const m1Response = await DonationApi.csc.getM1Percentage({
              startDate,
              endDate,
              campaignFilterValue,
              fundraiserFilterValue: fundraiserId,
            })

            const fundraiserName =
              `${metric.first_name || ''} ${metric.last_name || ''}`.trim() ||
              `Fundraiser ${fundraiserId}`

            m1Labels.push(fundraiserName)
            m1Percentages.push(m1Response.m1_percentage || 0)
          } catch (error) {
            console.error(`Error fetching M1 percentage for fundraiser:`, error)
          }
        }

        // Update M1 bar chart data
        m1BarChartData.value.labels = m1Labels
        m1BarChartData.value.datasets[0].data = m1Percentages

        // Build KPI cards for admin/npo
        kpis.value = buildAdminKpis(metrics, shiftsResponse)
      } catch (error) {
        console.error('Error fetching data:', error)
        const allChartData = [
          numberOfDonationsData,
          donorsOver30Data,
          averageDonorAgeData,
          averageDonationAmountData,
          numberOfOneTimeDonationsData,
          averageOneTimeDonationAmountData,
          oneTimeDonationSumData,
          shiftsSpentData,
          stopsByFundraiserData,
        ]
        allChartData.forEach((chart) => {
          chart.value.labels = []
          chart.value.datasets[0].data = []
        })

        alert('Failed to fetch data. Please try again later.')
      } finally {
        loading.value = false
      }
    }

    const updateChartData = (chartData, metrics, metricPath) => {
      try {
        if (!metrics || !Array.isArray(metrics)) {
          console.warn('Metrics data is not an array:', metrics)
          chartData.value.labels = []
          chartData.value.datasets[0].data = []
          return
        }

        const validMetrics = metrics.filter((item) => {
          try {
            const value = metricPath.split('.').reduce((obj, key) => obj[key], item)
            return value !== undefined && value !== null
          } catch {
            return false
          }
        })

        chartData.value.labels = validMetrics.map((f) => `${f.first_name} ${f.last_name}`)

        chartData.value.datasets[0].data = validMetrics.map((item) => {
          return metricPath.split('.').reduce((obj, key) => obj[key], item)
        })

        console.log(`Updated ${chartData.value.datasets[0].label} chart with:`, {
          labels: chartData.value.labels,
          data: chartData.value.datasets[0].data,
        })
      } catch (error) {
        console.error(`Error updating ${chartData.value.datasets[0].label} chart:`, error)
        chartData.value.labels = []
        chartData.value.datasets[0].data = []
      }
    }

    const handleDateChange = (dateField, value) => {
      selectedDate[dateField] = value
      const fundraiserFilterValue = selectedFundraiserFilter.value
        .map((fundraiser) => fundraiser.id)
        .join(',')
      fetchData(
        selectedDate.date1,
        selectedDate.date2,
        campaignFilterValue.value,
        fundraiserFilterValue,
      )
    }

    onBeforeMount(async () => {
      if (route.query.start) selectedDate.date1 = route.query.start
      if (route.query.end) selectedDate.date2 = route.query.end
      const fundraiserFilterValue = selectedFundraiserFilter.value
        .map((fundraiser) => fundraiser.id)
        .join(',')
      await fetchData(
        selectedDate.date1,
        selectedDate.date2,
        campaignFilterValue.value,
        fundraiserFilterValue,
      )
      const response = await DonationApi.csc.retrieveFilterIndexes()
      filterIndexes.campaigns = response.campaigns || []
    })
    watch(
      [() => selectedCampaignFilter.value, () => selectedFundraiserFilter.value],
      () => {
        setFilter()
      },
      { immediate: true },
    )

    return {
      loading,
      pageTitle,
      numberOfDonationsData,
      donorsOver30Data,
      averageDonorAgeData,
      averageDonationAmountData,
      numberOfOneTimeDonationsData,
      averageOneTimeDonationAmountData,
      oneTimeDonationSumData,
      shiftsSpentData,
      stopsByFundraiserData,
      selectedDate,
      handleDateChange,
      selectedCampaignFilter,
      campaignFilters,
      customLabel,
      chartOptions,
      dynamicChartWidth,
      kpis,
      showStopsByFundraiserChart,
      formTypePieChartData,
      pieChartOptions,
      dvSmsSentData,
      dvSmsVerifiedData,
      donorAgePieChartData,
      donationAmountPieChartData,
      agePieChartOptions,
      //  new returns
      selectedFundraiserFilter,
      fundraiserFilters,
      isSearchingFundraisers,
      handleFundraiserSearch,
      m1BarChartData,
    }
  },
}
</script>

<style scoped>
.multiselect {
  width: 378px;
}
.scrollable-chart-container {
  background-color: white;
  padding: 1rem;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  height: 600px;
  display: flex;
  flex-direction: column;
}

.chart-outer-wrapper {
  flex-grow: 1;
  overflow-x: auto;
  overflow-y: auto;
  position: relative;
}

.chart-inner-wrapper {
  height: 100%;
  min-width: 500px;
  margin: 0 auto;
}

/* Scrollbar styling */
.chart-outer-wrapper::-webkit-scrollbar {
  height: 8px;
  width: 8px;
}

.chart-outer-wrapper::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.chart-outer-wrapper::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.chart-outer-wrapper::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.title {
  font-size: 1.1rem;
  color: #333;
}
.pie-chart-container {
  background-color: white;
  padding: 1rem;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  height: 600px;
  display: flex;
  flex-direction: column;
}
</style>
